// =============================================================================
// features/relatorios/comercial/hooks/useSalesReportsOverview.ts — TanStack
// Query para GET /api/sales/reports/overview (F35-S12).
//
// Mesmo padrão de ../../hooks/useReportsOverview.ts: staleTime 3 min, sem
// retry em 403/404, expõe `isFetching`/`updatedAt` para o indicador de
// frescor (DoD: "dados stale identificados").
// =============================================================================
import type { SalesReportsOverviewResponse, SalesReportsQuery } from '@elemento/shared-schemas';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../../../lib/api';
import { fetchSalesReportsOverview } from '../api';

export const salesReportsKeys = {
  all: ['sales-reports'] as const,
  overview: (query: Partial<SalesReportsQuery>) =>
    [...salesReportsKeys.all, 'overview', query] as const,
};

interface UseSalesReportsOverviewResult {
  data: SalesReportsOverviewResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  updatedAt: number;
  isError: boolean;
  isForbidden: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useSalesReportsOverview(
  query: Partial<SalesReportsQuery> = {},
): UseSalesReportsOverviewResult {
  const { data, isLoading, isFetching, isError, error, dataUpdatedAt, refetch } = useQuery<
    SalesReportsOverviewResponse,
    Error
  >({
    queryKey: salesReportsKeys.overview(query),
    queryFn: () => fetchSalesReportsOverview(query),
    staleTime: 3 * 60_000,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return failureCount < 2;
    },
  });

  const isForbidden = isError && error instanceof ApiError && error.status === 403;

  return {
    data,
    isLoading,
    isFetching,
    updatedAt: dataUpdatedAt,
    isError,
    isForbidden,
    error,
    refetch,
  };
}
